// src/hooks/useSubCollection.js - ATUALIZADO

import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { 
    collection, 
    query, 
    onSnapshot, 
    addDoc, 
    deleteDoc, 
    updateDoc, 
    doc,
    orderBy 
} from 'firebase/firestore';

/**
 * Hook customizado para gerenciar uma subcoleção específica no Firestore.
 * * @param {string} parentCollection - Coleção pai (ex: 'parameters').
 * @param {string} parentId - ID do documento pai (ex: 'types').
 * @param {string} subCollectionName - Nome da subcoleção (ex: 'tipo-endereco').
 * @param {string} [orderByField='name'] - Campo para ordenação.
 */
const useSubCollection = (parentCollection, parentId, subCollectionName, orderByField = 'name') => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 🛑 ATUALIZAÇÃO CRÍTICA: Definir collectionRef dentro de useEffect para garantir
    // que o acesso ao Firebase só ocorra se os caminhos estiverem definidos.
    // O erro 'Cannot read properties of undefined (reading 'indexOf')' é evitado aqui.
    
    // CREATE/UPDATE/DELETE Funções (utilizam useCallback para consistência)
    
    // Função utilitária para obter a referência da subcoleção de forma segura
    const getCollectionRef = useCallback(() => {
        if (!parentCollection || !parentId || !subCollectionName) {
            return null;
        }
        return collection(db, parentCollection, parentId, subCollectionName);
    }, [parentCollection, parentId, subCollectionName]);


    // READ (Leitura em Tempo Real)
    useEffect(() => {
        // 🛑 NOVO: Verificação de segurança para o caminho
        if (!parentCollection || !parentId || !subCollectionName) {
            setLoading(false);
            setData([]);
            setError(new Error("Caminho da subcoleção incompleto ou inválido."));
            return;
        }

        const collectionRef = getCollectionRef();

        const q = query(collectionRef, orderBy(orderByField, 'asc'));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const items = [];
            querySnapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });
            setData(items);
            setLoading(false);
            setError(null);
        }, 
        (err) => {
            console.error("Erro ao carregar os dados:", err);
            setError("Falha ao carregar os dados.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [parentCollection, parentId, subCollectionName, orderByField, getCollectionRef]);

    // CREATE (Adicionar Documento)
    const addDocument = useCallback(async (documentData) => {
        const collectionRef = getCollectionRef();
        if (!collectionRef) {
             return { success: false, error: "Referência da coleção indisponível." };
        }
        try {
            await addDoc(collectionRef, documentData);
            return { success: true };
        } catch (e) {
            console.error("Erro ao adicionar documento: ", e);
            return { success: false, error: e.message };
        }
    }, [getCollectionRef]);

    // UPDATE (Editar Documento)
    const updateDocument = useCallback(async (id, documentData) => {
        const collectionRef = getCollectionRef();
        if (!collectionRef) {
             return { success: false, error: "Referência da coleção indisponível." };
        }
        try {
            const docRef = doc(collectionRef, id);
            await updateDoc(docRef, documentData);
            return { success: true };
        } catch (e) {
            console.error("Erro ao atualizar documento: ", e);
            return { success: false, error: e.message };
        }
    }, [getCollectionRef]);

    // DELETE (Deletar Documento)
    const deleteDocument = useCallback(async (id) => {
        const collectionRef = getCollectionRef();
        if (!collectionRef) {
             return { success: false, error: "Referência da coleção indisponível." };
        }
        try {
            await deleteDoc(doc(collectionRef, id));
            return { success: true };
        } catch (e) {
            console.error("Erro ao deletar documento: ", e);
            return { success: false, error: e.message };
        }
    }, [getCollectionRef]);
    
    return { data, loading, error, addDocument, updateDocument, deleteDocument };
};

export default useSubCollection;